import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import SearchIcon from "../../../../components/icons/SearchIcon";
import LoadingIcon from "../../../../components/icons/LoadingIcon";
import { PropertyApi } from "../../../../utils/api/Property";
import { getAgencyId, getAgentId } from "../../../../utils/helpers/common";
import { messages } from "../../../../config/messages";
import Loading from "../../../loading/Loading";
import "./EditPropertyBank.scss";
import PropertyBankActionMenu from "./PropertyBankActionMenu";
import ConfirmationModal from "./ConfirmationModal";
import ErrorSummary from "../error/ErrorSummary";

declare global {
  interface Window {
    google: any;
  }
}

interface PropertyType {
  _id: string;
  name: string;
  slug?: string;
}

interface SizeUnit {
  _id: string;
  name: string;
  symbol?: string;
}

const EditPropertyBank = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [searchOptions, setSearchOptions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recordData, setRecordData] = useState<any>(null);

  const [modalConfig, setModalConfig] = useState({
    open: false,
    title: "",
    description: "",
    actionType: "",
    newStatus: "",
  });

  const debounceTimeout = useRef<any>(null);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);

  const [state, setState] = useState<{
    isLoading: boolean;
    isSaving: boolean;
    isLoadingSelect: boolean;
    isUpdatingStatus: boolean;
    propertyType: {
      commercial: PropertyType[];
      residential: PropertyType[];
    };
    landSizeUnit: SizeUnit[];
    floorAreaUnit: SizeUnit[];
  }>({
    isLoading: true,
    isSaving: false,
    isLoadingSelect: false,
    isUpdatingStatus: false,
    propertyType: { commercial: [], residential: [] },
    landSizeUnit: [],
    floorAreaUnit: [],
  });

  const fetchPropertyData = async () => {
    if (!id) return;
    try {
      const recordRes = await PropertyApi.getPropertyBankById(id);
      if (recordRes?.data?.success) {
        setRecordData(recordRes.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
    }
  }, []);

  const validationSchema = useMemo(() => {
    return Yup.object({
      city: Yup.string().required(messages?.errorMessage),
      state: Yup.string().required(messages?.errorMessage),
      country: Yup.string().required(messages?.errorMessage),
      postCode: Yup.string().required(messages?.errorMessage),
      propertyType: Yup.string().required(messages?.homeType),

      bedRooms: Yup.string().required("Bedrooms are required"),
      bathRooms: Yup.string().required("Bathrooms are required"),

      landSize: Yup.string().test(
        "land-required",
        "Land size is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);

          if (!selectedType) return true;

          const { showLand } = getFieldVisibility(selectedType.name);

          if (showLand && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      landSizeUnit: Yup.string().test(
        "land-unit-required",
        "Land size unit is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showLand } = getFieldVisibility(selectedType.name);

          if (showLand && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      floorArea: Yup.string().test(
        "floor-required",
        "Floor area is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showFloor } = getFieldVisibility(selectedType.name);

          if (showFloor && (!val || val.trim() === "")) return false;
          return true;
        },
      ),

      floorAreaUnit: Yup.string().test(
        "floor-unit-required",
        "Floor area unit is required",
        function (val) {
          const { propertyType } = this.parent;
          if (!propertyType) return true;

          const allTypes = [
            ...state.propertyType.residential,
            ...state.propertyType.commercial,
          ];
          const selectedType = allTypes.find((t) => t._id === propertyType);
          if (!selectedType) return true;

          const { showFloor } = getFieldVisibility(selectedType.name);

          if (showFloor && (!val || val.trim() === "")) return false;
          return true;
        },
      ),
    });
  }, [state.propertyType]);

  const [initialValues, setInitialValues] = useState({
    searchAddress: "",
    unit: "",
    no: "",
    streetName: "",
    city: "",
    state: "",
    country: "",
    postCode: "",
    propertyType: "",
    landSize: "",
    landSizeUnit: "",
    floorArea: "",
    floorAreaUnit: "",
    bedRooms: "",
    bathRooms: "",
    living: "",
    garage: "",
  });

  useEffect(() => {
    const initData = async () => {
      try {
        const selectorsRes = await PropertyApi.getSelectors();
        const selectorsData = selectorsRes?.data?.data || {};

        setState((prev) => ({
          ...prev,
          propertyType: selectorsData.property_types || {
            commercial: [],
            residential: [],
          },
          landSizeUnit: selectorsData.land_size_units || [],
          floorAreaUnit: selectorsData.land_size_units || [],
        }));

        if (id) {
          const recordRes = await PropertyApi.getPropertyBankById(id);
          if (recordRes?.data?.success) {
            const data = recordRes.data.data;
            setRecordData(data);

            setInitialValues({
              searchAddress: data.property_address || "",
              unit: data.apartment_suite_unit_level || "",
              no: data.property_no || "",
              streetName: data.street_name || "",
              city: data.city || "",
              state: data.state || "",
              country: data.country || "Australia",
              postCode: data.post_code || "",

              propertyType:
                data.property_type_ref?._id || data.property_type_ref || "",
              landSize: data.land_area || "",
              landSizeUnit:
                data.land_size_unit?._id || data.land_size_unit || "",
              floorArea: data.floor_area || "",
              floorAreaUnit:
                data.floor_area_unit?._id || data.floor_area_unit || "",

              bedRooms: data.bedrooms || "0",
              bathRooms: data.bathrooms || "0",
              living: data.living || "0",
              garage: data.garage || "0",
            });
          } else {
            toast.error("Failed to load property details");
            navigate("/property-bank");
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong");
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initData();
  }, [id, navigate]);

  const handleAddressSearch = (value: string) => {
    if (value.length <= 0) {
      setSearchOptions([]);
      setShowDropdown(false);
      return;
    }
    setIsSearching(true);
    setShowDropdown(true);

    if (!autocompleteService.current) {
      setIsSearching(false);
      return;
    }

    autocompleteService.current.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: ["au", "nz", "gb"] },
        types: ["address"],
      },
      (predictions: any[], status: any) => {
        setIsSearching(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          const formattedGoogle = predictions.map((item: any) => ({
            id: item.place_id,
            displayLabel: item.description,
            originalData: item,
          }));
          setSearchOptions(formattedGoogle);
        } else {
          setSearchOptions([]);
        }
      },
    );
  };

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    formik.setFieldValue("searchAddress", val);
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => handleAddressSearch(val), 400);
  };

  const handleSelectOption = (option: any) => {
    setShowDropdown(false);
    formik.setFieldValue("searchAddress", option.displayLabel);

    if (placesService.current) {
      placesService.current.getDetails(
        {
          placeId: option.id,
          fields: ["address_components", "formatted_address"],
        },
        (place: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            fillGoogleData(place);
          }
        },
      );
    }
  };

  const fillGoogleData = (place: any) => {
    if (!place.address_components) return;

    const get = (type: string) =>
      place.address_components?.find((c: any) => c.types.includes(type))
        ?.long_name || "";

    formik.setFieldValue("unit", get("subpremise"));
    formik.setFieldValue("no", get("street_number"));
    formik.setFieldValue("streetName", get("route"));

    const city =
      get("locality") ||
      get("postal_town") ||
      get("sublocality_level_1") ||
      get("administrative_area_level_2");

    formik.setFieldValue("city", city);

    const stateVal =
      get("administrative_area_level_1") || get("administrative_area_level_2");
    formik.setFieldValue("state", stateVal);

    formik.setFieldValue("country", get("country") || "Australia");
    formik.setFieldValue("postCode", get("postal_code"));
  };

  const onSubmit = async (values: any) => {
    setState((prev) => ({ ...prev, isSaving: true }));

    try {
      const payload = {
        unit: values.unit || "",
        property_no: values.no || "",
        property_no_and_street_name: values.streetName || "",
        city: values.city || "",
        state: values.state || "",
        country: values.country || "",
        post_code: values.postCode || "",
        property_address: values.searchAddress || "",
        property_type_id: values.propertyType || "",
        land_area: values.landSize ? Number(values.landSize) : 0,
        land_size_unit_id: values.landSizeUnit || "",
        floor_area: values.floorArea ? Number(values.floorArea) : 0,
        floor_area_unit_id: values.floorAreaUnit || "",
        bedrooms: values.bedRooms ? Number(values.bedRooms) : 0,
        bathrooms: values.bathRooms ? Number(values.bathRooms) : 0,
        garage: values.garage ? Number(values.garage) : 0,
        living: values.living ? Number(values.living) : 0,
      };

      const res = await PropertyApi.updatePropertyBank(id as string, payload);

      if (res?.data?.success) {
        toast.success("Property Bank Record Updated!");
        navigate("/property-bank");
      } else {
        toast.error(res?.data?.message || "Failed to update record.");
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong.");
    } finally {
      setState((prev) => ({ ...prev, isSaving: false }));
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const formatStatus = (status: string) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case "agent_validated":
        return "status-validated";
      case "admin_draft":
      case "agent_draft":
      case "draft":
        return "status-draft";
      case "disabled":
      case "deactivated":
        return "status-disabled";
      default:
        return "status-draft";
    }
  };

  const handleCreatorClick = () => {
    if (!recordData) return;
    const creatorId = recordData.creator_id?._id;
    const role = recordData.creator_role;

    if (!creatorId) return;

    if (role === "agents") {
      navigate(`/agents/view/${creatorId}?type=info`);
    } else {
      navigate(`/users/view/${creatorId}?type=info`);
    }
  };

  const handleAgentClick = () => {
    const agentId = recordData?.agent_ref?._id;
    if (agentId) {
      navigate(`/agents/view/${agentId}?type=info`);
    }
  };

  const handleAgencyClick = () => {
    const agencyId = recordData?.agency_ref?._id;
    if (agencyId) {
      navigate(`/agencies/view/${agencyId}?type=info`);
    }
  };

  const getCreatedByLabel = () => {
    if (!recordData) return "-";

    if (recordData.creator_role === "admins" && recordData.creator_id) {
      const fname = recordData.creator_id.first_name || "";
      const lname = recordData.creator_id.last_name || "";
      return `${fname} ${lname}`.trim() + " (Admin)";
    }

    if (recordData.creator_role === "agents" && recordData.agent_ref) {
      const fname = recordData.agent_ref.principal_agent_first_name || "";
      const lname = recordData.agent_ref.principal_agent_last_name || "";
      return `${fname} ${lname}`.trim() + " (Agent)";
    }

    return "-";
  };

  const handleMenuAction = (newStatus: string) => {
    if (newStatus === "trash") {
      setModalConfig({
        open: true,
        title: "Delete property bank record?",
        description:
          "This action cannot be undone. \nAre you sure you want to permanently remove this?",
        actionType: "delete",
        newStatus: "trash",
      });
    } else if (newStatus === "disabled") {
      setModalConfig({
        open: true,
        title: "Disable property bank record?",
        description:
          "Are you sure you want to disable this? \nIt will no longer be active or visible to users.",
        actionType: "toggle",
        newStatus: "disabled",
      });
    } else {
      confirmStatusUpdate(newStatus);
    }
  };

  const confirmStatusUpdate = async (statusOverride?: string) => {
    const statusToSend = statusOverride || modalConfig.newStatus;

    setState((prev) => ({ ...prev, isUpdatingStatus: true }));
    try {
      const res = await PropertyApi.updatePropertyBank(id as string, {
        status: statusToSend,
      });

      if (res?.data?.success) {
        if (statusToSend === "trash") {
          toast.success("Property bank record deleted");
          navigate("/property-bank");
        } else {
          toast.success(
            `Property ${statusToSend === "disabled" ? "Disabled" : "Activated"}`,
          );
          await fetchPropertyData();
        }
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setState((prev) => ({ ...prev, isUpdatingStatus: false }));
      // Close Modal
      setModalConfig((prev) => ({ ...prev, open: false }));
    }
  };

  const getSelectedTypeName = () => {
    if (!formik.values.propertyType) return "";

    const list = [
      ...(state.propertyType.residential || []),
      ...(state.propertyType.commercial || []),
    ];

    const found = list.find((t) => t._id === formik.values.propertyType);

    return found?.name || "";
  };

  const selectedTypeName = getSelectedTypeName();

  const getFieldVisibility = (typeName: string) => {
    const name = typeName.toLowerCase();

    if (
      name.includes("house") ||
      name.includes("townhouse") ||
      name.includes("villa") ||
      name.includes("land") ||
      name.includes("rural") ||
      name.includes("acreage") ||
      name.includes("block of units")
    ) {
      return { showLand: true, showFloor: false };
    }

    if (
      name.includes("unit") ||
      name.includes("apartment") ||
      name.includes("retirement") ||
      name.includes("studio")
    ) {
      return { showLand: false, showFloor: true };
    }

    return { showLand: true, showFloor: true };
  };

  const { showLand, showFloor } = getFieldVisibility(selectedTypeName);

  useEffect(() => {
    if (!showLand) {
      formik.setFieldValue("landSize", "");
      formik.setFieldValue("landSizeUnit", "");
    }

    if (!showFloor) {
      formik.setFieldValue("floorArea", "");
      formik.setFieldValue("floorAreaUnit", "");
    }
  }, [showLand, showFloor]);

  useEffect(() => {
    if (!state.landSizeUnit.length) return;

    const targetUnit = state.landSizeUnit.find(
      (u) =>
        u.name.toLowerCase().includes("sqm") ||
        u.name.toLowerCase().includes("square meter"),
    );

    if (targetUnit) {
      if (showLand && !formik.values.landSizeUnit) {
        formik.setFieldValue("landSizeUnit", targetUnit._id);
      }

      if (showFloor && !formik.values.floorAreaUnit) {
        formik.setFieldValue("floorAreaUnit", targetUnit._id);
      }
    }
  }, [state.landSizeUnit, showLand, showFloor]);

  const shouldShowErrors =
    formik.submitCount > 0 && Object.keys(formik.errors).length > 0;

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="create-property-bank-container properties-container">
          {state.isLoading ? (
            <div
              style={{
                height: "80vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loading />
            </div>
          ) : (
            <>
              <div
                className="propertie-view-back-btn"
                onClick={() => navigate("/property-bank")}
              >
                <div className="back-icon">
                  <BackArrowIcon />
                </div>
                <p className="bakc-text-contain">Back to property bank list</p>
              </div>

              <div className="header-row">
                <h2 className="main-title">Property Info</h2>
                <div className="action-menu-btn">
                  <PropertyBankActionMenu
                    status={recordData?.status}
                    onStatusChange={handleMenuAction}
                    isLoading={state.isUpdatingStatus}
                  />
                </div>
              </div>

              {recordData && (
                <div className="property-meta-info">
                  <div className="meta-item font-inter-400">
                    <label>Property bank id</label>
                    <span>{recordData._id}</span>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Status</label>
                    <div
                      className={`status-badge font-inter-400 ${getStatusClass(recordData.status)}`}
                    >
                      {formatStatus(recordData.status)}
                    </div>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Created by</label>
                    <span
                      className="creator-name"
                      onClick={handleCreatorClick}
                      style={{ cursor: "pointer" }}
                    >
                      {getCreatedByLabel()}
                    </span>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Agent</label>
                    <span
                      className="creator-name"
                      onClick={handleAgentClick}
                      style={{ cursor: "pointer" }}
                    >
                      {recordData?.agent_ref?.fullname || "-"}
                    </span>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Agency</label>
                    <span
                      className="creator-name"
                      onClick={handleAgencyClick}
                      style={{ cursor: "pointer" }}
                    >
                      {recordData?.agency_ref?.agency_legal_name || "-"}
                    </span>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Created On</label>
                    <span>
                      {moment(recordData.createdAt).format(
                        "MMMM D, YYYY, hh:mm A",
                      )}
                    </span>
                  </div>
                  <div className="meta-item font-inter-400">
                    <label>Last Updated On</label>
                    <span>
                      {moment(recordData.updatedAt).format(
                        "MMMM D, YYYY, hh:mm A",
                      )}
                    </span>
                  </div>
                </div>
              )}

              <form onSubmit={formik.handleSubmit}>
                <div className="content-title font-inter-500">
                  Property address
                </div>
                <div className="content-subtitle font-inter-400">
                  Enter the address details for new property bank record.
                </div>

                <div className="search-input-container">
                  <span className="search-icon-wrapper">
                    <SearchIcon />
                  </span>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="default-input-contain with-search-icon"
                    placeholder="Search Address"
                    name="searchAddress"
                    value={formik.values.searchAddress}
                    onChange={onSearchInputChange}
                    autoComplete="off"
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    onFocus={() =>
                      formik.values.searchAddress.length > 2 &&
                      setShowDropdown(true)
                    }
                  />
                  {showDropdown &&
                    (searchOptions.length > 0 || isSearching) && (
                      <div className="custom-dropdown-list">
                        {isSearching ? (
                          <div className="dropdown-loading">
                            <CircularProgress
                              size={18}
                              style={{ marginRight: 10, color: "#333" }}
                            />{" "}
                            Searching...
                          </div>
                        ) : (
                          <ul>
                            {searchOptions.map((opt, i) => (
                              <li
                                key={i}
                                onClick={() => handleSelectOption(opt)}
                                className="dropdown-item"
                              >
                                <div className="icon-box">
                                  <LocationOnIcon
                                    sx={{ fontSize: 20, color: "#767279" }}
                                  />
                                </div>
                                <div className="text-box">
                                  <span className="main-text">
                                    {opt.displayLabel}
                                  </span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                </div>

                <div className="double-item-container">
                  <div className="input-and-lable-container first-lable">
                    <p className="label font-inter-400">
                      Apartment, Suite, Unit, Or Level
                    </p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="e.g. Unit 4, Level 2"
                      {...formik.getFieldProps("unit")}
                    />
                  </div>
                  <div className="input-and-lable-container first-lable">
                    <p className="label font-inter-400">
                      House Or Building Number
                    </p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="e.g. 22"
                      {...formik.getFieldProps("no")}
                    />
                  </div>
                </div>

                <div className="double-item-container">
                  <div
                    className={`input-and-lable-container ${formik.errors.streetName ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">Street Name</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="e.g. West Street"
                      {...formik.getFieldProps("streetName")}
                    />
                  </div>
                  <div
                    className={`input-and-lable-container ${formik.errors.city ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">City/Suburb*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="Select city"
                      {...formik.getFieldProps("city")}
                    />
                  </div>
                </div>

                <div className="double-item-container">
                  <div
                    className={`input-and-lable-container ${formik.errors.state ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">State*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="Enter state"
                      {...formik.getFieldProps("state")}
                    />
                  </div>
                  <div
                    className={`input-and-lable-container ${formik.errors.country ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">Country*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="Australia"
                      {...formik.getFieldProps("country")}
                    />
                  </div>
                </div>

                <div className="double-item-container">
                  <div
                    className={`input-and-lable-container ${formik.errors.postCode ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">Postcode*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="e.g. 2831"
                      {...formik.getFieldProps("postCode")}
                    />
                  </div>
                </div>

                <div className="specification-title font-inter-500">
                  Property Specifications
                </div>
                <div className="content-subtitle font-inter-400">
                  Select the property type and define key features like bedrooms
                  and bathrooms.
                </div>

                <div className="double-item-container">
                  <div
                    className={`input-and-lable-container specification-first-type ${formik.errors.propertyType ? "property-input-selector-border-error" : "property-input-selector-border"}`}
                  >
                    <p className="label font-inter-400">Property type*</p>
                    <FormControl fullWidth>
                      {!formik.values.propertyType && (
                        <InputLabel>Select property type</InputLabel>
                      )}
                      <Select
                        name="propertyType"
                        value={formik.values.propertyType}
                        onChange={formik.handleChange}
                        IconComponent={
                          state.isLoadingSelect
                            ? LoadingIcon
                            : KeyboardArrowDownIcon
                        }
                        className="create-property-forrm-selector"
                        MenuProps={{
                          disablePortal: true,
                          classes: { paper: "selector-menu-container" },
                        }}
                      >
                        {[...(state.propertyType.residential || [])].map(
                          (t: any) => (
                            <MenuItem key={t._id} value={t._id}>
                              {t.name}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {showLand && (
                  <div className="double-item-container">
                    <div
                      className={`input-and-lable-container property-info-inputs ${formik.errors.landSizeUnit ? "property-input-error" : ""}`}
                    >
                      <p className="label font-inter-400">Land size unit*</p>
                      <FormControl
                        fullWidth
                        className={`property-input-selector-border ${formik.errors.landSizeUnit ? "property-input-selector-border-error" : ""}`}
                      >
                        {!formik.values.landSizeUnit && (
                          <InputLabel>Square meter</InputLabel>
                        )}
                        <Select
                          name="landSizeUnit"
                          value={formik.values.landSizeUnit}
                          onChange={formik.handleChange}
                          IconComponent={KeyboardArrowDownIcon}
                          className="create-property-forrm-selector"
                          MenuProps={{
                            disablePortal: true,
                            classes: { paper: "selector-menu-container" },
                          }}
                        >
                          {state.landSizeUnit.map((u: any) => (
                            <MenuItem key={u._id} value={u._id}>
                              {u.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <div
                      className={`input-and-lable-container property-info-inputs ${formik.errors.landSize ? "property-input-error" : ""}`}
                    >
                      <p className="label font-inter-400">Land size*</p>
                      <input
                        type="text"
                        className="default-input-contain"
                        placeholder="0"
                        {...formik.getFieldProps("landSize")}
                      />
                    </div>
                  </div>
                )}
                {showFloor && (
                  <div className="double-item-container">
                    <div
                      className={`input-and-lable-container property-info-inputs ${formik.errors.floorAreaUnit ? "property-input-error" : ""}`}
                    >
                      <p className="label font-inter-400">Floor area unit*</p>
                      <FormControl
                        fullWidth
                        className={`property-input-selector-border ${formik.errors.floorAreaUnit ? "property-input-selector-border-error" : ""}`}
                      >
                        {!formik.values.floorAreaUnit && (
                          <InputLabel>Square meter</InputLabel>
                        )}
                        <Select
                          name="floorAreaUnit"
                          value={formik.values.floorAreaUnit}
                          onChange={formik.handleChange}
                          IconComponent={KeyboardArrowDownIcon}
                          className="create-property-forrm-selector"
                          MenuProps={{
                            disablePortal: true,
                            classes: { paper: "selector-menu-container" },
                          }}
                        >
                          {state.floorAreaUnit.map((u: any) => (
                            <MenuItem key={u._id} value={u._id}>
                              {u.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <div
                      className={`input-and-lable-container property-info-inputs ${formik.errors.floorArea ? "property-input-error" : ""}`}
                    >
                      <p className="label font-inter-400">Floor area*</p>
                      <input
                        type="text"
                        className="default-input-contain"
                        placeholder="0"
                        {...formik.getFieldProps("floorArea")}
                      />
                    </div>
                  </div>
                )}
                <div className="double-item-container">
                  <div
                    className={`input-and-lable-container property-info-inputs ${formik.errors.bedRooms ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">Bedrooms*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="0"
                      {...formik.getFieldProps("bedRooms")}
                    />
                  </div>
                  <div
                    className={`input-and-lable-container property-info-inputs ${formik.errors.bathRooms ? "property-input-error" : ""}`}
                  >
                    <p className="label font-inter-400">Bathrooms*</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="0"
                      {...formik.getFieldProps("bathRooms")}
                    />
                  </div>
                </div>

                <div className="double-item-container">
                  <div className="input-and-lable-container property-info-inputs">
                    <p className="label font-inter-400">Living/Study</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="0"
                      {...formik.getFieldProps("living")}
                    />
                  </div>
                  <div className="input-and-lable-container property-info-inputs">
                    <p className="label font-inter-400">Garage/Parking</p>
                    <input
                      type="text"
                      className="default-input-contain"
                      placeholder="0"
                      {...formik.getFieldProps("garage")}
                    />
                  </div>
                </div>

                {shouldShowErrors && (
                  <div className="error-message-component">
                    <ErrorSummary
                      errors={formik.errors as Record<string, string>}
                    />
                  </div>
                )}

                <div className="edit-property-button-container">
                  <button
                    type="button"
                    className="edit-cancel-property-button font-inter-400"
                    onClick={() => navigate("/property-bank")}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="edit-create-property-button font-inter-500"
                    disabled={state.isSaving}
                  >
                    {state.isSaving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </form>
            </>
          )}

          <ConfirmationModal
            open={modalConfig.open}
            title={modalConfig.title}
            description={modalConfig.description}
            onClose={() => setModalConfig((prev) => ({ ...prev, open: false }))}
            onConfirm={() => confirmStatusUpdate()}
            isLoading={state.isUpdatingStatus}
          />
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default EditPropertyBank;
